/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useMemo, useRef } from 'react'
import { useCombobox, UseComboboxProps } from 'downshift'
import { Icon, TextField } from '@alto-avios/alto-ui'
import { useMediaQuery } from 'usehooks-ts'
import {
  AutoCompleteContextValue,
  AutoCompleteProps,
  AutoCompleteItemAttributes,
  AUTOCOMPLETE_FIELD_ICON_PROPS,
} from './AutoComplete.types'
import { deviceSizes } from '../../../theme/deviceSizes'
import styles from './AutoComplete.module.scss'

const AutoCompleteContext = React.createContext<AutoCompleteContextValue | undefined>(undefined)

const AutoComplete = React.forwardRef(
  <TItem extends AutoCompleteItemAttributes>(
    props: AutoCompleteProps<TItem>,
    forwardedRef: React.ForwardedRef<HTMLInputElement>
  ) => {
    const {
      name,
      label,
      accessibleLabel,
      defaultValue,
      value,
      error,
      items,
      children,
      gridArea,
      onInputFocus,
      disabled,
      helperMessage,
      isRequired,
      endIconProps = AUTOCOMPLETE_FIELD_ICON_PROPS,
    } = props

    const firstMount = useRef(true)
    const isTabletOrSmaller = useMediaQuery(`(max-width: ${deviceSizes.medium})`)
    const computedAccessibleLabel = accessibleLabel ?? label

    const comboBoxOptions: UseComboboxProps<TItem> = {
      itemToString: (item: TItem | null) => item?.label || '',
      id: `field-${name}`,
      ...props,
    }

    // These must be added conditionally,
    // otherwise the component swaps from uncontrolled to controlled
    if (defaultValue) {
      comboBoxOptions.defaultInputValue = defaultValue
    }
    if (value) {
      comboBoxOptions.initialInputValue = value
    }

    const {
      getInputProps,
      getMenuProps,
      getItemProps,
      isOpen,
      openMenu,
      setInputValue,
      highlightedIndex,
      setHighlightedIndex,
      inputValue,
      closeMenu,
    } = useCombobox(comboBoxOptions)

    // Create context value for option elements
    const contextValue = useMemo<AutoCompleteContextValue>(
      () => ({
        highlightedIndex,
        getOptionProps: (item: TItem) => {
          const index = items.indexOf(item)
          return {
            ...getItemProps({ item, index }),
          }
        },
        closeMenu,
      }),
      [highlightedIndex, closeMenu, items, getItemProps]
    )

    const { ref, ...inputProps } = getInputProps({
      'aria-autocomplete': 'both',
      onFocus: (e) => {
        if (onInputFocus) {
          onInputFocus(e, openMenu)
        }
      },
    })

    useEffect(() => {
      const nextInputValue = value ?? defaultValue
      if (nextInputValue && nextInputValue !== inputValue) {
        setInputValue(nextInputValue)
      }
      if (value && inputValue !== value && isTabletOrSmaller) {
        closeMenu()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closeMenu, setInputValue, defaultValue, value, isTabletOrSmaller])

    const inputRefCallback = React.useCallback(
      (el: HTMLInputElement | null) => {
        // pass ref element to forwardedRef if it exists
        if (typeof forwardedRef === 'function') {
          forwardedRef(el)
        } else if (forwardedRef) {
          forwardedRef.current = el
        }
        // pass ref element to downshift
        if (typeof ref === 'function') {
          ref(el)
        }
      },
      [ref, forwardedRef]
    )

    useEffect(() => {
      const input = (forwardedRef as React.MutableRefObject<HTMLInputElement | null>)?.current
      if (isTabletOrSmaller && input && firstMount.current) {
        firstMount.current = false
        input.focus()
      }
    }, [isTabletOrSmaller, forwardedRef])

    const { 'aria-labelledby': _ariaLabelledBy, ...safeInputProps } = inputProps

    return (
      <AutoCompleteContext.Provider value={contextValue}>
        <TextField
          ref={inputRefCallback}
          style={{ gridArea }}
          errorMessage={error}
          defaultValue={defaultValue}
          name={name}
          type="text"
          value={value}
          isDisabled={disabled}
          isRequired={isRequired}
          isInvalid={!!error}
          {...safeInputProps}
          label={label}
          aria-label={computedAccessibleLabel}
          onChange={(newValue) => {
            setHighlightedIndex(-1)
            setInputValue(newValue)
            openMenu()
          }}
        >
          <TextField.EndIcon>
            <Icon {...endIconProps} isDecorative />
          </TextField.EndIcon>
        </TextField>
        <div className={styles.popupWrapper}>
          <ul className={styles.popup} {...getMenuProps()}>
            {isOpen && children}
          </ul>
        </div>
        {helperMessage}
      </AutoCompleteContext.Provider>
    )
  }
)

export function useAutoCompleteContext() {
  return React.useContext(AutoCompleteContext)
}

export function useComboboxOption<TItem extends AutoCompleteItemAttributes>(
  item: TItem,
  index: number
) {
  const context = React.useContext(AutoCompleteContext)
  if (!context) {
    throw new Error('useOptionProps must be used within an AutoComplete component')
  }
  return { props: context.getOptionProps(item), isHighlighted: index === context.highlightedIndex }
}

export default AutoComplete
